import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS, REACTION_COLORS, modalBackdropSlotProps } from "./colors";
import CommentThread from "./CommentThread";
import { deleteCommentOfDay, replyToCommentOfDay, updateReplyOfDay } from "../../service/service";

const ReplyComposer = ({ authorName, onCancel, onSubmit, submitting }) => {
  const [text, setText] = useState("");

  return (
    <Box sx={{ ml: 4, mb: 1.5, p: 1.5, bgcolor: REACTION_COLORS.insightful.bg, borderRadius: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Replying to {authorName}
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        placeholder="Write a reply…"
        value={text}
        onChange={(event) => setText(event.target.value)}
        autoFocus
        sx={{ mb: 1, bgcolor: COLORS.cardBackground }}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!text.trim() || submitting}
          onClick={() => onSubmit(text.trim())}
        >
          Post Reply
        </Button>
        <Button variant="outlined" size="small" sx={{ color: COLORS.primaryText, borderColor: COLORS.primaryText }} onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

const ReplyEditor = ({ initialText, onCancel, onSubmit, submitting }) => {
  const [text, setText] = useState(initialText);

  return (
    <Box sx={{ ml: 4, mb: 1.5 }}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        value={text}
        onChange={(event) => setText(event.target.value)}
        autoFocus
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!text.trim() || submitting}
          onClick={() => onSubmit(text.trim())}
        >
          Save
        </Button>
        <Button variant="outlined" size="small" sx={{ color: COLORS.primaryText, borderColor: COLORS.primaryText }} onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

const CommentsLightbox = ({ open, onClose, tipId, tipTitle, comments, focusCommentId, onCommentsChanged }) => {
  const [replyingToId, setReplyingToId] = useState(null);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && focusCommentId) {
      setReplyingToId(focusCommentId);
      setEditingReplyId(null);
      const el = document.getElementById(`comment-${focusCommentId}`);
      if (el) {
        el.scrollIntoView({ block: "center" });
      }
    }
  }, [open, focusCommentId]);

  const handleClose = () => {
    setReplyingToId(null);
    setEditingReplyId(null);
    onClose();
  };

  const handlePostReply = async (commentId, text) => {
    setSubmitting(true);
    const result = await replyToCommentOfDay(tipId, commentId, text);
    setSubmitting(false);
    if (result?.success !== false) {
      setReplyingToId(null);
      onCommentsChanged();
    }
  };

  const handleSaveReplyEdit = async (replyId, text) => {
    setSubmitting(true);
    const result = await updateReplyOfDay(replyId, text);
    setSubmitting(false);
    if (result?.success !== false) {
      setEditingReplyId(null);
      onCommentsChanged();
    }
  };

  const handleDelete = async (commentId) => {
    await deleteCommentOfDay(commentId);
    onCommentsChanged();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        ...modalBackdropSlotProps,
        paper: { sx: { width: 760, maxWidth: "92vw", height: 620, display: "flex", flexDirection: "column" } },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Comments ({comments.length})
          </Typography>
          {tipTitle && (
            <Typography variant="body2" color="text.secondary">
              {tipTitle}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {comments.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No comments yet.
          </Typography>
        )}
        {comments.map((comment) => (
          <Box key={comment._id}>
            <CommentThread
              comment={comment}
              idPrefix="comment"
              editingReplyId={editingReplyId}
              onReply={replyingToId === comment._id ? undefined : (selected) => setReplyingToId(selected._id)}
              onEditReply={(reply) => setEditingReplyId(reply._id)}
              onDelete={handleDelete}
            />

            {replyingToId === comment._id && !comment.reply && (
              <ReplyComposer
                authorName={comment.authorName}
                submitting={submitting}
                onCancel={() => setReplyingToId(null)}
                onSubmit={(text) => handlePostReply(comment._id, text)}
              />
            )}

            {comment.reply && editingReplyId === comment.reply._id && (
              <ReplyEditor
                initialText={comment.reply.text}
                submitting={submitting}
                onCancel={() => setEditingReplyId(null)}
                onSubmit={(text) => handleSaveReplyEdit(comment.reply._id, text)}
              />
            )}

            <Divider sx={{ my: 1.5 }} />
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default CommentsLightbox;
